import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

export interface NPO {
  id: string;
  name: string;
  description: string;
  country: string;
  type: string;
  meta: Record<string, any>;
  // Other NPO fields can be added here
}

export interface NPOsResponse {
  data: NPO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CountriesResponse {
  data: string[];
}

export interface TypesResponse {
  data: string[];
}

export interface StatesResponse {
  success: boolean;
  data: string[];
}

export interface DistrictsResponse {
  success: boolean;
  data: string[];
}

export interface SubDistrictsResponse {
  success: boolean;
  data: string[];
}

interface NPOsQueryParams {
  page?: number;
  limit?: number;
  country?: string;
  type?: string;
  search?: string;
  meta?: string | string[] | Record<string, any>;
}

export function useNPOsApi(params: NPOsQueryParams = {}) {
  return useQuery<NPOsResponse>({
    queryKey: ["npos", params],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      if (params.page) queryParams.append("page", params.page.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      
      // Add filter params
      if (params.country) queryParams.append("country", params.country);
      if (params.type) queryParams.append("type", params.type);
      if (params.search) queryParams.append("search", params.search);
      
      // Handle meta filters
      if (params.meta) {
        if (typeof params.meta === "string") {
          queryParams.append("meta", params.meta);
        } else if (Array.isArray(params.meta)) {
          queryParams.append("meta", JSON.stringify(params.meta));
        } else if (typeof params.meta === "object") {
          // Handle object-type meta parameters in the format meta[key]=value
          Object.entries(params.meta).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              queryParams.append(`meta[${key}]`, value.toString());
            }
          });
        }
      }
      
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos?${queryParams.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as NPOsResponse;
    },
  });
}

export function useInfiniteNPOsApi(params: NPOsQueryParams = {}, options: { enabled?: boolean } = {}) {
  return useInfiniteQuery<NPOsResponse>({
    queryKey: ["infinite-npos", params],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const queryParams = new URLSearchParams();
      
      // Add pagination params
      queryParams.append("page", pageParam.toString());
      if (params.limit) queryParams.append("limit", params.limit.toString());
      
      // Add filter params
      if (params.country) queryParams.append("country", params.country);
      if (params.type) queryParams.append("type", params.type);
      if (params.search) queryParams.append("search", params.search);
      
      // Handle meta filters
      if (params.meta) {
        if (typeof params.meta === "string") {
          queryParams.append("meta", params.meta);
        } else if (Array.isArray(params.meta)) {
          queryParams.append("meta", JSON.stringify(params.meta));
        } else if (typeof params.meta === "object") {
          // Handle object-type meta parameters in the format meta[key]=value
          Object.entries(params.meta).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              queryParams.append(`meta[${key}]`, value.toString());
            }
          });
        }
      }
      
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos?${queryParams.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as NPOsResponse;
    },
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    ...options // Spread additional options like enabled
  });
}

/**
 * Hook to fetch available NPO countries
 */
export function useNPOCountries() {
  return useQuery<CountriesResponse>({
    queryKey: ["npo-countries"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos/countries`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as CountriesResponse;
    },
  });
}

/**
 * Hook to fetch available NPO types
 */
export function useNPOTypes() {
  return useQuery<TypesResponse>({
    queryKey: ["npo-types"],
    queryFn: async () => {
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos/types`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as TypesResponse;
    },
  });
}

/**
 * Hook to fetch available NPO states with optional country filter
 */
export function useNPOStates(country?: string) {
  return useQuery<StatesResponse>({
    queryKey: ["npo-states", country],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (country) queryParams.append("country", country);
      
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos/states${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as StatesResponse;
    },
    enabled: true, // Always fetch states, even if country is undefined
  });
}

/**
 * Hook to fetch available NPO districts with optional state and country filters
 */
export function useNPODistricts(state?: string, country?: string) {
  return useQuery<DistrictsResponse>({
    queryKey: ["npo-districts", state, country],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (state) queryParams.append("state", state);
      if (country) queryParams.append("country", country);
      
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos/districts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as DistrictsResponse;
    },
    enabled: !!state, // Only fetch districts if state is provided
  });
}

/**
 * Hook to fetch available NPO sub-districts with optional district, state, and country filters
 */
export function useNPOSubDistricts(district?: string, state?: string, country?: string) {
  return useQuery<SubDistrictsResponse>({
    queryKey: ["npo-sub-districts", district, state, country],
    queryFn: async () => {
      const queryParams = new URLSearchParams();
      if (district) queryParams.append("district", district);
      if (state) queryParams.append("state", state);
      if (country) queryParams.append("country", country);
      
      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/npos/sub-districts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      return await response.json() as SubDistrictsResponse;
    },
    enabled: !!district, // Only fetch sub-districts if district is provided
  });
}
